import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, Eye, Database, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function LoanPrivacyPage() {
  const navigate = useNavigate();

  const dataCollected = [
    'Personal information (Name, BVN, NIN, Date of Birth)',
    'Contact details (Phone, Email, Address)',
    'Financial information (Bank statements, Income details)',
    'Employment information (Employer, Salary, Job title)',
    'Credit history and repayment behavior',
    'Transaction patterns and account activity'
  ];

  const dataUsage = [
    'Assessing loan eligibility and creditworthiness',
    'Processing loan applications and disbursements',
    'Credit scoring and risk assessment',
    'Fraud prevention and security monitoring',
    'Regulatory compliance and reporting',
    'Communication regarding loan status'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="pt-32 pb-20 px-4 md:px-8 bg-gradient-to-br from-indigo-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <Shield className="w-16 h-16 text-brand-blue mx-auto mb-6" />
          <h1 className="text-5xl md:text-7xl font-bold font-heading text-slate-900 mb-6">
            Loan Privacy Policy
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            How we collect, use, and protect your information during the loan application and management process
          </p>
          <p className="text-sm text-slate-500 mt-4">Last Updated: December 2024</p>
        </div>
      </section>

      <section className="py-20 px-4 md:px-8">
        <div className="max-w-4xl mx-auto prose prose-lg">
          <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">1. Introduction</h2>
          <p className="text-slate-600 leading-relaxed mb-8">
            At Britedge, we understand that applying for a loan requires sharing sensitive personal and financial information. This Loan Privacy Policy explains how we collect, use, store, and protect your data throughout the loan lifecycle. We are committed to transparency and compliance with the Nigeria Data Protection Regulation (NDPR) and CBN guidelines.
          </p>

          <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">2. Information We Collect</h2>
          <div className="bg-slate-50 rounded-2xl p-6 mb-8">
            <p className="text-slate-600 mb-4">When you apply for a loan with Britedge, we collect:</p>
            <ul className="space-y-2">
              {dataCollected.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-slate-700">
                  <Database className="w-5 h-5 text-brand-blue flex-shrink-0 mt-1" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">3. How We Use Your Information</h2>
          <div className="bg-indigo-50 rounded-2xl p-6 mb-8 border border-blue-100">
            <p className="text-slate-600 mb-4">Your loan data is used exclusively for:</p>
            <ul className="space-y-2">
              {dataUsage.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-slate-700">
                  <Eye className="w-5 h-5 text-brand-blue flex-shrink-0 mt-1" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">4. Credit Bureau Reporting</h2>
          <div className="bg-yellow-50 rounded-2xl p-6 mb-8 border border-yellow-200">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-12 h-12 text-yellow-600 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-slate-900 mb-2">Important Notice</h3>
                <p className="text-slate-700 leading-relaxed">
                  By applying for a loan, you authorize Britedge to report your credit information to licensed credit bureaus in Nigeria (CRC Credit Bureau, FirstCentral Credit Bureau, etc.). This includes loan amounts, repayment history, and default records. This information may affect your credit score and future borrowing capacity.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">5. Data Security Measures</h2>
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <Lock className="w-10 h-10 text-green-600 mb-4" />
              <h3 className="font-bold text-slate-900 mb-2">Encryption</h3>
              <p className="text-sm text-slate-600">All loan data is encrypted using AES-256 encryption in transit and at rest</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <Shield className="w-10 h-10 text-blue-600 mb-4" />
              <h3 className="font-bold text-slate-900 mb-2">Access Control</h3>
              <p className="text-sm text-slate-600">Strict role-based access controls limit who can view your loan information</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <Database className="w-10 h-10 text-purple-600 mb-4" />
              <h3 className="font-bold text-slate-900 mb-2">Secure Storage</h3>
              <p className="text-sm text-slate-600">Data stored in CBN-compliant, geographically distributed servers</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <Eye className="w-10 h-10 text-orange-600 mb-4" />
              <h3 className="font-bold text-slate-900 mb-2">Monitoring</h3>
              <p className="text-sm text-slate-600">24/7 security monitoring and threat detection systems</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">6. Data Sharing</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            We may share your loan information with:
          </p>
          <ul className="space-y-3 mb-8 text-slate-700">
            <li><strong>Credit Bureaus:</strong> For credit reporting and verification</li>
            <li><strong>Banking Partners:</strong> For loan disbursement and repayment collection</li>
            <li><strong>Regulatory Authorities:</strong> CBN, NDIC, and other regulators as required by law</li>
            <li><strong>Collection Agencies:</strong> Only in case of default, for debt recovery</li>
            <li><strong>Service Providers:</strong> Third-party processors under strict confidentiality agreements</li>
          </ul>
          <p className="text-slate-600 leading-relaxed mb-8">
            <strong>We will never</strong> sell your loan information to marketers or unrelated third parties.
          </p>

          <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">7. Your Rights</h2>
          <div className="bg-slate-50 rounded-2xl p-6 mb-8">
            <p className="text-slate-600 mb-4">Under NDPR, you have the right to:</p>
            <ul className="space-y-2 text-slate-700">
              <li className="flex items-start gap-2">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-brand-blue flex items-center justify-center flex-shrink-0 text-sm font-bold">✓</span>
                <span>Access your loan data and credit reports</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-brand-blue flex items-center justify-center flex-shrink-0 text-sm font-bold">✓</span>
                <span>Request correction of inaccurate information</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-brand-blue flex items-center justify-center flex-shrink-0 text-sm font-bold">✓</span>
                <span>Object to processing (may affect loan approval)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-brand-blue flex items-center justify-center flex-shrink-0 text-sm font-bold">✓</span>
                <span>Request data deletion (after loan closure and regulatory retention periods)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-brand-blue flex items-center justify-center flex-shrink-0 text-sm font-bold">✓</span>
                <span>Withdraw consent (may result in loan rejection or recall)</span>
              </li>
            </ul>
          </div>

          <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">8. Data Retention</h2>
          <p className="text-slate-600 leading-relaxed mb-8">
            We retain your loan data for:
          </p>
          <ul className="space-y-2 mb-8 text-slate-700">
            <li><strong>Active Loans:</strong> Duration of loan + 6 months after full repayment</li>
            <li><strong>Rejected Applications:</strong> 12 months from rejection date</li>
            <li><strong>Defaulted Loans:</strong> 7 years as required by CBN regulations</li>
            <li><strong>Credit Bureau Records:</strong> As per credit bureau retention policies</li>
          </ul>

          <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">9. Contact Us</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            For questions about this Loan Privacy Policy or to exercise your rights:
          </p>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
            <p className="text-slate-700"><strong>Data Protection Officer</strong></p>
            <p className="text-slate-600">Email: dpo@britedge.ng</p>
            <p className="text-slate-600">Phone: +234 (0) 800 BRITEDGE</p>
            <p className="text-slate-600">Address: Plot 123, Admiralty Way, Lekki Phase 1, Lagos</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
